import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, Subject, take } from 'rxjs';
import { LanguageEndpoint } from '../endpoints/language';
import { ILanguage, IPagination } from '../models';
import { TranslateService } from '@ngx-translate/core';

/**
 * ### **LanguageService**
 *
 * Un Servicio en Angular es el mecanismo para compartir funcionalidad entre componentes, en este caso se encarga de compartir las funciones
 * para obtener la infromación en el idioma respectivo, estas hacen diferentes peticiones o llamados a distintos API's que genera el backend
 */
@Injectable({
  providedIn: 'root',
})
export class Language {
  private readonly http = inject(HttpClient);
  private readonly translateService = inject(TranslateService);

  /** Variable que guardará el idioma actual */
  private language = new BehaviorSubject<'es' | 'en'>('es');

  /** Cadena con el lenguaje actual de la página */
  private actualLang = 'es';

  /** Subject que permite cambiar el lenguaje actual de la página */
  private changeActualLang$ = new Subject<string>();

  /** @ignore*/
  constructor() {}

  /**
   * Función que permite cambiar el lenguaje actual de la página
   * @param lang Cadena con la abreviatura del lenguage
   */
  onChangeActualLang(lang: string): void {
    this.changeActualLang$.next(lang);
  }

  /**
   * Función que obtiene un observable que permite saber el lenguaje actual
   * de la página
   */
  getChangeActualLang$(): Observable<string> {
    return this.changeActualLang$.asObservable();
  }

  /**
   * Función que permite obtener el idioma actual
   * @returns Devuelve un observable con el idioma actual
   */
  getLanguage(): Observable<'es' | 'en'> {
    return this.language.asObservable();
  }

  /**
   * Función que permite cambiar el idioma actual
   * @param language Cadena con el valor del idioma
   */
  setLanguage(language: 'es' | 'en') {
    this.language.next(language);
  }

  /** Función encargada de obtener la lista de titulos y subtitulos en un idioma especificado*/
  getLanguages(): Observable<Array<ILanguage>> {
    return this.http.get<Array<ILanguage>>(LanguageEndpoint.languages);
  }

  /** Función encargada de obtener la lista de titulos y subtitulos en un idioma especificado*/
  getLanguages2(): Observable<IPagination<ILanguage[]>> {
    return this.http.get<IPagination<ILanguage[]>>(LanguageEndpoint.languages2);
  }

  /** Función que obtiene el lenguaje actual de la página */
  getActualLanguage(): string {
    return this.actualLang;
  }

  /**
   * Función que configura el lenguaje de la página
   * @param lang Cadena con el lenguaje
   */
  setActualLanguage(lang: string): void {
    this.actualLang = lang;
  }

  /**
   * Función que permite obtener un valor de un label traducido
   * @param keyLabel Cadena con el valor de la llave del label
   * @returns Devuelve una cadena con el label solicitado traducido
   */
  getTranslate = async (keyLabel: string, params?: any): Promise<string> => {
    return new Promise<string>((r, j) => {
      this.translateService
        .get(keyLabel, params)
        .pipe(take(1))
        .subscribe((label) => r(label));
    });
  };
}
